<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div
        v-if="isSuccess"
        :class="{ 'full-height': !items.length }"
      >
        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber}}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong> {{ $t('Common.companies') }}
          </div>

          <v-layout class="table__header" align-center>
            <v-flex xs1 />
            <v-flex xs1>ID</v-flex>
            <v-flex xs4>{{ $t('Common.socialReason') }}</v-flex>
            <v-flex xs3>{{ $t('Common.companyFantasyName') }}</v-flex>
            <v-flex xs3>CNPJ</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                @click="handleClick($event, item)"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex xs1>
                        <v-tooltip
                          v-if="hasRole('company', 'ROLE_PUT_COMPANY_ID_DISABLE')"
                          slot="append"
                          min-width="130"
                          max-width="130"
                          top
                        >
                          <v-btn
                            color="gray-6"
                            class="mx-0"
                            slot="activator"
                            flat
                            icon
                            @click="handleEnableCompany(item)"
                          >
                            <v-icon>lock_open</v-icon>
                          </v-btn>
                          <span>{{ $t(`${namespace}.enableCompany`) }}</span>
                        </v-tooltip>

                        <v-tooltip
                          v-if="hasRole('company', 'ROLE_PUT_COMPANY_ID_DISABLE') && hasRole('anonymization', 'ROLE_POST_LGPD_ANONYMIZATION_REQUEST')"
                          slot="append"
                          min-width="130"
                          max-width="130"
                          top
                        >
                          <v-btn
                            color="gray-6"
                            class="mx-0"
                            slot="activator"
                            flat
                            icon
                            @click="handleRemoveCompany(item)"
                          >
                            <v-icon>delete</v-icon>
                          </v-btn>
                          <span>{{ $t(`${namespace}.removeCompany`) }}</span>
                        </v-tooltip>
                      </v-flex>

                      <v-flex xs1>
                        <span :title="item.id" class="text--ellipsis" contenteditable>
                          {{ item.id }}
                        </span>
                      </v-flex>
                      <v-flex xs4>
                        <span class="text--ellipsis" :title="item.name">
                          {{ item.name | ellipsis(40) }}
                        </span>
                      </v-flex>
                      <v-flex xs3>
                        <span class="text--ellipsis" :title="item.displayName">
                          {{ item.displayName | ellipsis(40) }}
                        </span>
                      </v-flex>
                      <v-flex xs3>
                        <span class="text--ellipsis" :title="item.documentId | cnpj">
                          {{ item.documentId | cnpj }}
                        </span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="cost-center"
          :text="$t(`${namespace}.noneFound`)"
          style="padding-bottom: 120px"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          style="margin-bottom: 120px"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>

    <ChangeCompanyStatusDialog
      :visible="changeCompanyStatusDialog"
      :company="company"
      @close="closeChangeCompanyStatusDialog"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { hasRole, formatNumber } from 'utils/functions'
import EmptyState from 'components/EmptyState'
import ChangeCompanyStatusDialog from 'components/company/disabledCompanies/ChangeCompanyStatusDialog'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    }
  },
  components: {
    EmptyState,
    ChangeCompanyStatusDialog
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.CompanyPage.disabledCompanies',
      changeCompanyStatusDialog: false,
      company: null
    }
  },
  methods: {
    hasRole (group, role) {
      return hasRole(group, role)
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-btn') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.$router.push({
          name: 'CompanyDetail',
          params: { id: item.id }
        })
      }
    },

    openChangeCompanyStatusDialog (company) {
      this.company = company
      this.changeCompanyStatusDialog = true
    },

    closeChangeCompanyStatusDialog (reload = false) {
      this.company = null
      this.changeCompanyStatusDialog = false
      if (reload) this.handleReloadData()
    },

    handleEnableCompany (company) {
      this.openChangeCompanyStatusDialog({
        id: company.id,
        status: 'enabled'
      })
    },

    handleRemoveCompany (company) {
      this.openChangeCompanyStatusDialog({
        id: company.id,
        status: 'disable'
      })
    }
  }
}
</script>
